import { useState } from 'react'
import { Button, Form, Input, InputNumber, Select, Switch, Tag, Space, App } from 'antd'
import { PlusOutlined, EditOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { countsApi } from '@/features/masters/counts/api'
import type { CountMaster } from '@/shared/types/master'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const COUNT_TYPE_OPTIONS = [
  'CARDED', 'COMBED', 'COMPACT', 'OPEN_END', 'BLENDED',
].map((t) => ({ label: t.replace('_', ' '), value: t }))

export default function CountsPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [page, setPage] = useState(1)
  const [typeFilter, setTypeFilter] = useState<string | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<CountMaster | null>(null)

  const canCreate = usePermission('count.create')
  const canUpdate = usePermission('count.update')

  const { data, isLoading } = useQuery({
    queryKey: ['counts', page, typeFilter],
    queryFn: () => countsApi.list({ page, page_size: 20, count_type: typeFilter }),
  })

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldValue('is_active', true)
    setDrawerOpen(true)
  }

  const openEdit = (record: CountMaster) => {
    setEditing(record)
    form.setFieldsValue(record)
    setDrawerOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: (values: Omit<CountMaster, 'id'>) =>
      editing ? countsApi.update(editing.id, values) : countsApi.create(values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['counts'] })
      message.success(editing ? 'Count updated' : 'Count created')
      setDrawerOpen(false)
    },
    onError: () => message.error('Save failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const columns = [
    { title: 'Count (Ne)', dataIndex: 'count_ne', key: 'count_ne' },
    { title: 'Type', dataIndex: 'count_type', key: 'count_type' },
    { title: 'Description', dataIndex: 'description', key: 'description', render: (v?: string) => v ?? '—' },
    { title: 'Std Cost/kg', dataIndex: 'standard_cost_per_kg', key: 'standard_cost_per_kg', render: (v?: string) => v ?? '—' },
    { title: 'Price Floor/kg', dataIndex: 'price_floor_per_kg', key: 'price_floor_per_kg', render: (v?: string) => v ?? '—' },
    { title: 'SITRA GPS', dataIndex: 'sitra_gps_target', key: 'sitra_gps_target', render: (v?: string) => v ?? '—' },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: CountMaster) =>
        canUpdate ? (
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Edit
          </Button>
        ) : null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Count Master"
        subtitle="Manage yarn count definitions and benchmarks"
        extra={
          canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Count
            </Button>
          )
        }
      />

      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Filter by type"
          options={COUNT_TYPE_OPTIONS}
          onChange={(v) => { setTypeFilter(v); setPage(1) }}
          style={{ width: 180 }}
        />
      </Space>

      <DataTable
        rowKey="id"
        columns={columns}
        dataSource={data?.items}
        loading={isLoading}
        total={data?.total}
        page={page}
        pageSize={20}
        onPageChange={(p) => setPage(p)}
      />

      <FormDrawer
        title={editing ? 'Edit Count' : 'New Count'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={onSubmit}
        loading={saveMutation.isPending}
        form={form}
        width={540}
      >
        <Form.Item name="count_ne" label="Count (Ne)" rules={[{ required: true }]}>
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="count_type" label="Count Type" rules={[{ required: true }]}>
          <Select options={COUNT_TYPE_OPTIONS} placeholder="Select type" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input />
        </Form.Item>
        <Form.Item name="standard_cost_per_kg" label="Standard Cost/kg">
          <InputNumber min={0} precision={4} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="price_floor_per_kg" label="Price Floor/kg">
          <InputNumber min={0} precision={4} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="sitra_gps_target" label="SITRA GPS Target">
          <InputNumber min={0} precision={3} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="sitra_eb_per_100sh" label="SITRA EB per 100 Spindle Hours">
          <InputNumber min={0} precision={3} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="sitra_ukg_target" label="SITRA UKg Target">
          <InputNumber min={0} precision={3} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="is_active" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </FormDrawer>
    </div>
  )
}
